import {
  GROUPS_FETCH_GROUP_SETS,
  GROUPS_FETCH_GROUP_SETS_NO_GROUP_STUDENTS,
  GROUPS_EDIT_GROUP,
} from '../actions';
import {
  fetchGroupSets as fetchGroupSetsApi,
  fetchGroupSet as fetchGroupSetApi,
} from '../api';
import fetchStudentsWithNoGroupPerSet from '../api/fetchStudentsWithNoGroupPerSet';
import refreshAccessToken from '../../actions/accessToken/refreshAccessToken.action';
import { currentClassRoomId as getCurrentClassRoomId } from '../../selectors/classroom';

const fetchGroupSetsStarted = (payload) => ({
  type: GROUPS_FETCH_GROUP_SETS,
  status: 'started',
  payload,
});

const editGroupSuccess = (payload) => ({
  type: GROUPS_EDIT_GROUP,
  status: 'success',
  payload,
});

const fetchGroupSetsSuccess = (payload) => ({
  type: GROUPS_FETCH_GROUP_SETS,
  status: 'success',
  payload,
});

const fetchGroupSetsError = (payload) => ({
  type: GROUPS_FETCH_GROUP_SETS,
  status: 'error',
  payload,
});

const fetchGroupSetsNoGroupStudentsSuccess = (payload) => ({
  type: GROUPS_FETCH_GROUP_SETS_NO_GROUP_STUDENTS,
  status: 'success',
  payload,
});

const fetchGroupSets =
  (space = undefined) =>
  async (dispatch, getState) => {
    const state = getState();
    const spaceId = space || getCurrentClassRoomId(state);
    const payload = { spaceId };
    dispatch(fetchGroupSetsStarted(payload));
    try {
      const accessToken = await refreshAccessToken(
        dispatch,
        getState
      )({ spaceId });

      let groupSets = await fetchGroupSetsApi(accessToken, {
        spaceId: payload.spaceId,
      });

      // Fetch group details (names, counts) — needed for page render
      const groupsData = await Promise.all(
        groupSets.map((set) =>
          fetchGroupSetApi(accessToken, { groupSetId: set.id })
        )
      );

      groupSets = groupSets.map((set, index) => ({
        ...set,
        groups: groupsData[index].groups,
      }));

      // Dispatch success immediately so the page renders with group names/counts
      dispatch(fetchGroupSetsSuccess({ groupSets }));

      // When groups already have a conversation on the server, the initial
      // API responses may not include conversationId. Poll fetchGroupSet per
      // set until conversationId appears.
      groupSets.forEach((set) => {
        const groupsNeedingConvId = (set.groups ?? []).filter(
          (g) => g?.hasConversation && !g?.conversationId
        );
        if (!groupsNeedingConvId.length) return;

        const poll = async (retries = 8, delay = 600) => {
          if (retries === 0) return;
          await new Promise((resolve) => setTimeout(resolve, delay));
          try {
            const updatedSet = await fetchGroupSetApi(accessToken, {
              groupSetId: set.id,
            });
            const updatedGroups = updatedSet?.groups ?? [];
            const updates = groupsNeedingConvId
              .map((g) => {
                const found = updatedGroups.find((ug) => ug.id === g.id);
                if (found?.conversationId) {
                  return {
                    id: g.id,
                    set: set.id,
                    conversationId: found.conversationId,
                  };
                }
                return null;
              })
              .filter(Boolean);
            if (updates.length) {
              updates.forEach((u) => {
                dispatch(
                  editGroupSuccess({
                    group: u,
                    studentsWithNoGroup: undefined,
                  })
                );
              });
            }
          } catch {
            // silently ignore
          }
        };
        poll();
      });

      // Fetch studentsWithNoGroup in the background — only needed for Edit/Create dialogs
      Promise.all(
        groupSets.map(async (set) => {
          try {
            const data = await fetchStudentsWithNoGroupPerSet(
              accessToken,
              set.id,
              payload.spaceId
            );
            return { setId: set.id, data };
          } catch {
            return { setId: set.id, data: {} };
          }
        })
      ).then((noGroupStudentsPerSet) => {
        dispatch(
          fetchGroupSetsNoGroupStudentsSuccess({ noGroupStudentsPerSet })
        );
      });
    } catch (error) {
      dispatch(fetchGroupSetsError({ ...payload, error }));
    }
  };

export default fetchGroupSets;
